package com.neurobs.ccabserver.translation.components.phraseselection

import com.neurobs.ccab.db.PhraseGroupData
import com.neurobs.ccab.translation.TranslatorDataView
import com.neurobs.react.semanticui.{UiCheckboxFactory, UiDropdownFactory, UiGridFactory}
import com.neurobs.react.{ReactUtil, StyleUtil}
import com.neurobs.ccabserver.translation.states.{PhraseGroupSelectionStateGenerator, TranslatorStateGenerator}
import com.neurobs.ccabserver.translation.util.PhraseGroupManager
import com.neurobs.react.ReactElementFactory
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.ReactElement

import scala.scalajs.js

@react
object PhraseGroupSelection {

  case class Props(enclosingElement: (ReactElement*) => ReactElement)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState
    val phraseGroupSelectionState = PhraseGroupSelectionStateGenerator.accessState

    def awaitingServerPhraseGroups = phraseGroupSelectionState.awaitingServerPhraseGroups

    if (translatorState.selectedTranslator.isDefined) {
      props.enclosingElement(
          if (awaitingServerPhraseGroups) Seq(LoadingDropdown())
          else phraseGroupSelectionState.phraseGroups match {
            case Left(errorOpt) => Seq(UiDropdownFactory.DisabledDropdown(errorOpt.getOrElse("Error loading server tests"), classString = "error"))
            case Right(phraseGroups) =>
              Seq(
                ReportButton(),
                PhraseGroupDropdown(phraseGroups)
              )
          }
      )

    } else null
  }
}

@react
object LoadingDropdown {
  case class Props()

  val component = FunctionalComponent[Props] { _ =>

    UiDropdownFactory.LoadingDropdown("Loading tests")
  }
}

@react
object PhraseGroupStatusCheckbox {
  case class Props()

  val component = FunctionalComponent[Props] { _ =>

    val phraseGroupSelectionState = PhraseGroupSelectionStateGenerator.accessState

    def filterOnTranslationStatus = phraseGroupSelectionState.filterOnTranslationStatus
    def toggleFilterStatus = () => phraseGroupSelectionState.toggleFilterStatus()

    def checkboxStyle = Seq[(String, js.Any)](("color", "white"), ("marginBottom", "10px"))

    UiCheckboxFactory.SingleOptionCheckbox(
      "Only show incomplete tests",
      toggleFilterStatus,
      filterOnTranslationStatus
    )(checkboxStyle)
  }
}

@react
object PhraseGroupDropdown {
  case class Props(phraseGroups: List[PhraseGroupManager])

  val component = FunctionalComponent[Props] { props =>

    val phraseGroupSelectionState = PhraseGroupSelectionStateGenerator.accessState

    def phraseGroupSelections = {
      if (phraseGroupSelectionState.filterOnTranslationStatus) {
        props.phraseGroups.filter(_.needsTranslation.getOrElse(true))
      } else props.phraseGroups

    }
    def selectedPhraseGroup = phraseGroupSelectionState.selectedPhraseGroup

    def handleSelect(phraseGroup: PhraseGroupManager) = phraseGroupSelectionState.setSelectedPhraseGroup(phraseGroup)

    def dropdownItem(phraseGroup: PhraseGroupManager) = {

      def selector = () => handleSelect(phraseGroup)
      def needsTranslationOpt = phraseGroup.needsTranslation
      def spanStyle(needsTranslation: Boolean) = Seq[(String, js.Any)](("color", if (needsTranslation) "red" else "green"))

      List(
        UiDropdownFactory.DropdownItem(
          handleClick = selector,
          keyString = phraseGroup.phraseGroupId.toString,
        )(
          UiDropdownFactory.DropdownText(phraseGroup.phraseGroup.displayName),
          ReactElementFactory.Br,
          if (needsTranslationOpt.isDefined) UiDropdownFactory.DropdownSubtext("Translation status: ")
          else null,
          needsTranslationOpt match {
            case None => null
            case Some(needsTranslation) => UiDropdownFactory.DropdownSubtext(if (needsTranslation) "incomplete" else "complete", styleAttributes = spanStyle(needsTranslation))
          }
        )
      )
    }

    UiDropdownFactory.SearchSelectionDropdown[PhraseGroupManager](
      placeholder = "Select test",
      selections = phraseGroupSelections,
      selected = selectedPhraseGroup,
      itemGenerator = dropdownItem(_),
      displayGenerator = _.phraseGroupDisplayName
    )
  }
}

@react
object ReportButton {
  case class Props()
  val component = FunctionalComponent[Props] { _ =>

    val translatorState = TranslatorStateGenerator.accessState
    val phraseGroupSelectionState = PhraseGroupSelectionStateGenerator.accessState

    def translatorAndPhraseGroup = translatorState.selectedTranslator.flatMap(translator => phraseGroupSelectionState.selectedPhraseGroup.map((translator, _)))

    def createFileBasename(phraseGroup: PhraseGroupData, translator: TranslatorDataView) = {
      s"${phraseGroup.displayName}_${translator.languageDisplayName}_translation"
    }

    def gridStyling = Seq[(String, js.Any)](("marginBottom", "10px"))

    translatorAndPhraseGroup match {
      case None => null
      case Some((translator, phraseGroup)) =>
        val fileBasename = createFileBasename(phraseGroup.phraseGroup, translator)
        UiGridFactory.Grid(
          UiGridFactory.GridColumn(
            ReactElementFactory.A(
              text = "Generate .docx report",
              href = s"/api/v1/translations/report?phrase_group_id=${phraseGroup.phraseGroupId}&translator_id=${translator.id}&doc_filename=${fileBasename}.docx",
              target = "_blank"
            )("white-text")
          )("eight wide center aligned"),
          UiGridFactory.GridColumn(
            ReactElementFactory.A(
              text = "Generate .xlsx report",
              href = s"/api/v1/translations/report?phrase_group_id=${phraseGroup.phraseGroupId}&translator_id=${translator.id}&doc_filename=${fileBasename}.xlsx",
              target = "_blank"
            )("white-text")
          )("eight wide center aligned"),
        )(gridStyling)
    }


  }
}

